import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from '../../redux/store';
import { getJobs, getLevels, upsertJob } from '../../redux/slices/settings';
import styles from '../../Components/EditDiv/EditDiv.module.css';
import buttonStyles from '../../Components/YesNoButtonsCSS/YesNoButtons.module.css';
import BlueLogo from '../../assets/BlueLogo.png';
import SliderSwitch from '../../Components/SliderSwitch/SliderSwitch';
import Dropdown from '../../Components/DropDownSelect/Dropdown';

function JobTitlesPopup({ onClose }) {
  const dispatch = useDispatch();
  const { jobs, isJobsLoading, levels } = useSelector((state) => state.settings);

  const [jobSearch, setJobSearch] = useState('');
  const [jobLevelFilterId, setJobLevelFilterId] = useState('');
  const [newJobRows, setNewJobRows] = useState([]); // { tempId, title, level_id, active }

  useEffect(() => {
    dispatch(getJobs());
    dispatch(getLevels());
  }, [dispatch]);

  const filteredJobs = useMemo(() => {
    const levelLabelById = new Map((levels || []).map((l) => [String(l.id), l.level]));
    const parseLevelNumber = (raw) => {
      const val = (raw || '').toString().trim();
      if (!val) return NaN;
      const m = val.match(/^[Ll]\s*\.?\s*(\d+(?:\.\d+)?)/) || val.match(/^level\s*(\d+(?:\.\d+)?)/i) || val.match(/^(\d+(?:\.\d+)?)/);
      return m ? parseFloat(m[1]) : NaN;
    };

    let list = Array.isArray(jobs) ? jobs.slice() : [];
    if (jobSearch.trim()) {
      const term = jobSearch.trim().toLowerCase();
      list = list.filter((j) => (j.title || '').toLowerCase().includes(term));
    }
    if (jobLevelFilterId) {
      const idNum = Number(jobLevelFilterId);
      list = list.filter((j) => (j.level_id === idNum));
    }
    list.sort((a, b) => {
      const la = a.level_code || levelLabelById.get(String(a.level_id));
      const lb = b.level_code || levelLabelById.get(String(b.level_id));
      const an = (() => { const n = parseLevelNumber(la); return isNaN(n) ? Infinity : n; })();
      const bn = (() => { const n = parseLevelNumber(lb); return isNaN(n) ? Infinity : n; })();
      if (an !== bn) return an - bn;
      return (a.title || '').localeCompare(b.title || '');
    });
    return list;
  }, [jobs, jobSearch, jobLevelFilterId, levels]);

  const addNewJobRow = () => {
    const defaultLevelId = (levels && levels[0] && levels[0].id) ? levels[0].id : null;
    const tempId = `new-${Date.now()}-${Math.floor(Math.random()*1000)}`;
    setNewJobRows((prev) => ([...prev, { tempId, title: '', level_id: defaultLevelId, active: true }]));
  };

  const updateNewJobRow = (tempId, field, value) => {
    setNewJobRows((prev) => prev.map((r) => r.tempId === tempId ? { ...r, [field]: value } : r));
  };

  const saveNewJobRow = async (tempId) => {
    const row = newJobRows.find((r) => r.tempId === tempId);
    if (!row) return;
    const title = (row.title || '').trim();
    if (!title || !row.level_id) return;
    await dispatch(upsertJob({ title, level_id: row.level_id, active: row.active }));
    setNewJobRows((prev) => prev.filter((r) => r.tempId !== tempId));
  };

  const cancelNewJobRow = (tempId) => {
    setNewJobRows((prev) => prev.filter((r) => r.tempId !== tempId));
  };

  const handleChangeJobLevel = async (jobId, title, levelId) => {
    await dispatch(upsertJob({ id: jobId, title, level_id: levelId }));
  };

  return (
    <div className={styles.backGround} style={{ position: 'fixed', alignItems: 'flex-start' }}>
      <div className={styles.form} style={{ width: '720px' }}>
        <div className={styles.momentTitle}>MOMENT</div>
        <div className={styles.sectionTitle}>Manage Job Titles</div>

        {/* Filters row */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', margin: '10px 0', padding: '10px 12px', background: '#f5f9fd', border: '1px solid #e3eef9', borderRadius: '8px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: '1 1 auto' }}>
              <input
                className={styles.Comment}
                style={{ height: '20px', flex: '1 1 auto' }}
                placeholder="Search job titles"
                value={jobSearch}
                onChange={(e) => setJobSearch(e.target.value)}
              />
              <div style={{ minWidth: '200px' }}>
                <Dropdown
                  options={[ 'All levels', ...(levels || []).map((lvl) => lvl.level) ]}
                  selectedValue={(function sel(){
                    if (!jobLevelFilterId) return 'All levels';
                    const match = (levels || []).find((l) => String(l.id) === String(jobLevelFilterId));
                    return match ? match.level : 'All levels';
                  })()}
                  onValueChange={(label) => {
                    if (label === 'All levels') { setJobLevelFilterId(''); return; }
                    const match = (levels || []).find((l) => l.level === label);
                    setJobLevelFilterId(match ? String(match.id) : '');
                  }}
                />
              </div>
          </div>
        </div>

        {/* Add job row trigger */}
        <div style={{ display: 'flex', justifyContent: 'flex-start', margin: '8px 0 14px' }}>
          <div className={styles.updateButton} onClick={(e) => { e.preventDefault(); addNewJobRow(); }}>
            <div className={buttonStyles.YesButtonComponentStyling}>ADD JOB ROW</div>
          </div>
        </div>

        <div style={{ width: '100%', overflow: 'auto', border: '1px solid #e7e7e7', borderRadius: '8px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={{ position: 'sticky', top: 0, background: '#fafafa', textAlign: 'left', padding: '10px', borderBottom: '1px solid #e0e0e0' }}>Title</th>
                <th style={{ position: 'sticky', top: 0, background: '#fafafa', textAlign: 'left', padding: '10px', borderBottom: '1px solid #e0e0e0' }}>Level</th>
                <th style={{ position: 'sticky', top: 0, background: '#fafafa', width: '120px', padding: '10px', borderBottom: '1px solid #e0e0e0', textAlign: 'center' }}>Active</th>
              </tr>
            </thead>
            <tbody>
              {isJobsLoading ? (
                <tr><td colSpan={3} style={{ padding: '16px', textAlign: 'center' }}>Loading...</td></tr>
              ) : (Array.isArray(filteredJobs) && filteredJobs.length > 0 ? (
                filteredJobs.map((j, idx) => (
                  <tr key={j.id} style={{ background: idx % 2 === 0 ? '#ffffff' : '#fbfbfb' }}>
                    <td style={{ padding: '10px' }}>{j.title}</td>
                    <td style={{ padding: '10px' }}>
                      <select
                        className={styles.Comment}
                        style={{ height: '24px' }}
                        value={(() => {
                          if (j.level_id) return j.level_id;
                          const match = (levels || []).find((l) => (l.level === j.level_code));
                          return match?.id ?? '';
                        })()}
                        onChange={(e) => handleChangeJobLevel(j.id, j.title, Number(e.target.value))}
                      >
                        {(levels || []).map((lvl) => (
                          <option key={lvl.id} value={lvl.id}>{lvl.level}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <SliderSwitch
                          checked={!!j.active}
                          onToggle={(checked) => {
                            const currentLevelId = j.level_id ?? (levels || []).find((l) => l.level === j.level_code)?.id ?? null;
                            dispatch(upsertJob({ id: j.id, title: j.title, level_id: currentLevelId, active: checked }));
                          }}
                        />
                        <span style={{ fontSize: '12px', color: '#555' }}>{j.active ? 'Shown' : 'Hidden'}</span>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={3} style={{ padding: '16px', textAlign: 'center', color: '#777' }}>No job titles found.</td></tr>
              ))}
              {/* New job draft rows */}
              {newJobRows.map((r) => (
                <tr key={r.tempId} style={{ background: '#fffdf5' }}>
                  <td style={{ padding: '10px' }}>
                    <input
                      className={styles.Comment}
                      style={{ height: '20px', width: '100%' }}
                      placeholder="Enter job title"
                      value={r.title}
                      onChange={(e) => updateNewJobRow(r.tempId, 'title', e.target.value)}
                    />
                  </td>
                  <td style={{ padding: '10px' }}>
                    <select
                      className={styles.Comment}
                      style={{ height: '24px' }}
                      value={r.level_id ?? ''}
                      onChange={(e) => updateNewJobRow(r.tempId, 'level_id', Number(e.target.value))}
                    >
                      {(levels || []).map((lvl) => (
                        <option key={lvl.id} value={lvl.id}>{lvl.level}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginRight: '10px' }}>
                      <SliderSwitch
                        checked={!!r.active}
                        onToggle={(checked) => updateNewJobRow(r.tempId, 'active', checked)}
                      />
                      <span style={{ fontSize: '12px', color: '#555' }}>{r.active ? 'Shown' : 'Hidden'}</span>
                    </span>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); saveNewJobRow(r.tempId); }}
                      title="Save"
                      style={{
                        backgroundColor: '#2e7d32',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '14px',
                        padding: '2px 10px',
                        cursor: 'pointer',
                        marginRight: '6px'
                      }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); cancelNewJobRow(r.tempId); }}
                      title="Cancel"
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid #e0e0e0',
                        color: '#555',
                        borderRadius: '14px',
                        padding: '2px 10px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.updateAndCancel}>
          <div className={styles.cancelButton} onClick={onClose}>
            <div className={buttonStyles.NoButtonComponentStyling}>CLOSE</div>
          </div>
        </div>

        <img className={styles.blueLogo} src={BlueLogo} alt="BlueLogo" />
      </div>
    </div>
  );
}

export default JobTitlesPopup;


